// Shared by the Pay Inputs import wizards (Shift Earnings, Leave): Map
// Columns > Next checks the rows in the browser, then with the screen's
// import/validate API (nothing saved); this merges the server's answer in.

// Merges the server check (an import validate API) into the browser check.
// `results` are the wizard's row checks; `serverRows` answer the rows that
// passed the browser check, in that order (row.index = position among them).
// A row the server refuses becomes an error row with the server's message;
// a row it accepts takes the server's amount and payroll cycle.
// `recordKey` is where a result keeps its API record ("row" for Leave,
// "record" for Shift).
export const applyServerChecks = (results, serverRows, recordKey = "row") => {
  const byIndex = new Map(serverRows.map((row) => [row.index, row]));
  let position = -1;
  return results.map((result) => {
    if (!result[recordKey]) return result;
    position += 1;
    const server = byIndex.get(position);
    if (!server) return { ...result, [recordKey]: null, errors: ["Not checked by the server"] };
    if (server.status !== "valid") return { ...result, [recordKey]: null, errors: [server.message || "Not valid"] };
    return {
      ...result,
      [recordKey]: {
        ...result[recordKey],
        payable_amount: server.amount ?? result[recordKey].payable_amount,
        pay_schedule_name: server.pay_schedule_name || "",
      },
    };
  });
};

// Excel date/time cells, read with cellNF + raw (no cellDates: xlsx turns
// date cells into local-time Dates that land a few seconds before midnight
// in IST, so 05 Oct reads as 04 Oct). Date cells become "YYYY-MM-DD" from
// the cell's own day number. Time-only cells (a format with no day or year,
// e.g. 2:30 typed as h:mm or [h]:mm) become "HH:MM" text instead of a
// 1900 date. `SSF` is XLSX.SSF.
export const convertDateTimeCells = (sheet, SSF) => {
  Object.keys(sheet).forEach((address) => {
    const cell = sheet[address];
    if (address[0] === "!" || cell.t !== "n" || !cell.z || !SSF.is_date(cell.z)) return;
    const format = String(cell.z).replace(/\[[^\]]*\]|"[^"]*"/g, "").toLowerCase();
    if (!/[dy]/.test(format)) {
      const minutes = Math.round(cell.v * 24 * 60);
      cell.t = "s";
      cell.v = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
      delete cell.w;
      return;
    }
    const day = SSF.parse_date_code(cell.v);
    if (!day) return;
    cell.t = "s";
    cell.v = `${day.y}-${String(day.m).padStart(2, "0")}-${String(day.d).padStart(2, "0")}`;
    delete cell.w;
  });
  return sheet;
};

// "HH:MM" (or H:MM) -> decimal hours (02:30 -> 2.5), rounded to 4 places;
// null for anything else.
export const hhmmToHours = (value) => {
  const match = /^(\d{1,3}):([0-5]\d)$/.exec(String(value ?? "").trim());
  return match ? Math.round((Number(match[1]) + Number(match[2]) / 60) * 10000) / 10000 : null;
};
